import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postsApi, categoriesApi, tagsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { ArrowLeft, Save, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    subtitle: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    featured: false,
    reading_time: 5,
    status: 'draft',
    published_at: '',
  });

  useEffect(() => {
    fetchMetadata();
    if (!isNew) {
      fetchPost();
    }
  }, [id]);

  const fetchMetadata = async () => {
    try {
      const [cats, tgs] = await Promise.all([
        categoriesApi.getCategories(),
        tagsApi.getTags(),
      ]);
      setCategories(cats);
      setTags(tgs);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  const fetchPost = async () => {
    try {
      const post = await postsApi.getPost(id);
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        subtitle: post.subtitle || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        category: post.category || '',
        tags: post.tags || [],
        featured: post.featured || false,
        reading_time: post.reading_time || 5,
        status: post.status || 'draft',
        published_at: post.published_at
          ? new Date(post.published_at).toISOString().slice(0, 16)
          : '',
      });
    } catch (error) {
      toast.error('Yazı yüklenirken hata oluştu');
      navigate('/admin/posts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateLocalSlug = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/ı/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-');
  };

  const handleTitleChange = (title) => {
    handleChange('title', title);
    if (isNew) {
      handleChange('slug', generateLocalSlug(title));
    }
  };

  const handleTagToggle = (tagSlug) => {
    const newTags = formData.tags.includes(tagSlug)
      ? formData.tags.filter(t => t !== tagSlug)
      : [...formData.tags, tagSlug];
    handleChange('tags', newTags);
  };

  const handleSave = async (publish = false) => {
    if (!formData.title || !formData.slug || !formData.excerpt || !formData.content || !formData.category) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        status: publish ? 'published' : formData.status,
        // Boş string gönderilmesini engelle: sadece gerçek tarih varsa gönder
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : undefined,
      };

      if (isNew) {
        const created = await postsApi.createPost(data);
        toast.success(publish ? 'Yazı yayınlandı' : 'Yazı kaydedildi');
        navigate(`/admin/posts/${created._id}`);
      } else {
        await postsApi.updatePost(id, data);
        if (publish && formData.status !== 'published') {
          await postsApi.publishPost(id);
        }
        toast.success(publish ? 'Yazı yayınlandı' : 'Değişiklikler kaydedildi');
        fetchPost();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaydetme sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    
    setSaving(true);
    try {
      await postsApi.unpublishPost(id);
      handleChange('status', 'draft');
      toast.success('Yazı yayından kaldırıldı');
    } catch (error) {
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/posts')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl font-bold text-foreground">
              {isNew ? 'Yeni Yazı' : 'Yazıyı Düzenle'}
            </h1>
            {!isNew && (
              <span className={`text-xs ${formData.status === 'published' ? 'text-accent' : 'text-muted-foreground'}`}>
                {formData.status === 'published' ? 'Yayında' : 'Taslak'}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {formData.status === 'published' && (
            <Button variant="outline" onClick={handleUnpublish} disabled={saving}>
              <EyeOff className="h-4 w-4 mr-2" />
              Yayından Kaldır
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
          {formData.status !== 'published' && (
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Eye className="h-4 w-4 mr-2" />
              Yayınla
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Başlık *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Yazı başlığı"
              className="text-lg font-serif"
            />
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="slug">URL Slug *</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="yazi-basligi"
            />
          </div>

          {/* Subtitle */}
          <div className="space-y-2">
            <Label htmlFor="subtitle">Alt Başlık</Label>
            <Input
              id="subtitle"
              value={formData.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              placeholder="Opsiyonel alt başlık"
            />
          </div>

          {/* Excerpt */}
          <div className="space-y-2">
            <Label htmlFor="excerpt">Özet (Tez) *</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Yazının ana tezi veya özeti..."
              rows={3}
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label>İçerik *</Label>
            <RichTextEditor
              content={formData.content}
              onChange={(html) => handleChange('content', html)}
              placeholder="Yazınızı buraya yazın..."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Kategori *</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat.slug}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Etiketler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag._id}
                    type="button"
                    onClick={() => handleTagToggle(tag.slug)}
                    className={`px-2 py-1 text-xs rounded-sm border transition-colors ${
                      formData.tags.includes(tag.slug)
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'bg-background border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Ayarlar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="featured" className="cursor-pointer">Öne Çıkan</Label>
                <Switch
                  id="featured"
                  checked={formData.featured}
                  onCheckedChange={(checked) => handleChange('featured', checked)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reading_time">Okuma Süresi (dk)</Label>
                <Input
                  id="reading_time"
                  type="number"
                  min="1"
                  value={formData.reading_time}
                  onChange={(e) => handleChange('reading_time', parseInt(e.target.value) || 5)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Yayın Tarihi */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Yayın Tarihi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="published_at" className="text-xs text-muted-foreground">
                Bu alanı doldurursanız yazı bu tarihte yayınlanmış kabul edilir.
              </Label>
              <Input
                id="published_at"
                type="datetime-local"
                value={formData.published_at || ''}
                onChange={(e) => handleChange('published_at', e.target.value)}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;
